import type { ClassificationIssue } from '@features/assessment/components/workflow/ClassificationWarningModal';
import { injectable } from 'inversify';
import { makeAutoObservable } from 'mobx';

import { AssessmentDomainStatus } from '@libs/constants';
import { GetAssessmentByIdResponse } from '@libs/types';

/** Mirrors `NavigationType` from `useNavigationGuard`. Duplicated to avoid a
 *  circular import (store ← hook ← store). */
type NavigationType = 'back' | 'next' | 'exit' | 'domain' | 'resume' | 'resumeNext';

/**
 * Derived flags about the currently-displayed domain. Published by the
 * workflow page on each render so child components (DomainNavigation,
 * DomainDetails) can observe instead of receiving 10+ flag props.
 */
export interface CurrentDomainFlags {
  isDomainInProgress: boolean;
  isDomainCompleted: boolean;
  isDomainFieldsDisabled: boolean;
  isFirstDomain: boolean;
  isLastDomain: boolean;
  allDomainsCompleted: boolean;
  uploadedDocumentsCount: number;
  isSaveAndNextDisabled: boolean;
  isCurrentDomainRequired: boolean;
  currentDomainStatus?: AssessmentDomainStatus;
  nextDomainStatus?: AssessmentDomainStatus;
}

const DEFAULT_DOMAIN_FLAGS: CurrentDomainFlags = {
  isDomainInProgress: false,
  isDomainCompleted: false,
  isDomainFieldsDisabled: false,
  isFirstDomain: false,
  isLastDomain: false,
  allDomainsCompleted: false,
  uploadedDocumentsCount: 0,
  isSaveAndNextDisabled: false,
  isCurrentDomainRequired: false,
  currentDomainStatus: undefined,
  nextDomainStatus: undefined,
};

/**
 * Action bundle published by `useNavigationGuard` for the SaveChangesModal.
 * The layout renders the modal as an observer and reads this bundle directly,
 * so the page no longer wires save / discard / stay props per render.
 */
export interface SaveChangesModalHandlers {
  isLoading: boolean;
  destinationType?: NavigationType;
  onSaveAndMove: () => void | Promise<void>;
  onDiscardAndMove: () => void | Promise<void>;
  onStay: () => void;
}

/**
 * Action bundle published by `useDocumentClassification` for the
 * ClassificationWarningModal.
 */
export interface ClassificationWarningHandlers {
  issues: ClassificationIssue[];
  onSaveAnyway: () => void;
  onCancel: () => void;
}

/**
 * Action bundle published by `useAssessmentExitFlow` for the
 * ExitAssessmentModal — handlers + the stats shown in the modal body.
 */
export interface ExitModalHandlers {
  isLoading: boolean;
  completionPercentage: number;
  completedDomainsCount: number;
  totalDomains: number;
  filesAttachedCount: number;
  onCancel: () => void;
  onSaveAndExit: () => void | Promise<void>;
  onDeleteAndExit: () => void | Promise<void>;
}

@injectable()
export class AssessmentWorkflowStore {
  public assessmentId: string | null = null;
  public assessment: GetAssessmentByIdResponse | null = null;
  public isNavigationDisabled = false;
  public nodeClickGuard: ((index: number) => void) | null = null;
  public exitGuard: (() => void) | null = null;

  /**
   * Modal open flags. Hooks toggle these via `open*` / `close*` actions
   * rather than holding local React state, so the layout's modal renderers
   * can simply observe the store.
   */
  public isExitModalOpen = false;
  public isSaveChangesModalOpen = false;
  public isClassificationWarningOpen = false;

  /**
   * Handler/data bundles registered by the workflow hooks. `null` while the
   * workflow page isn't mounted (e.g. on the summary/overview pages).
   */
  public saveChangesModalHandlers: SaveChangesModalHandlers | null = null;
  public classificationWarningHandlers: ClassificationWarningHandlers | null = null;
  public exitModalHandlers: ExitModalHandlers | null = null;

  /**
   * Derived flags about the active domain (published by the workflow page on
   * each render). Children read these via `observer` instead of receiving the
   * full set as props.
   */
  public currentDomainFlags: CurrentDomainFlags = DEFAULT_DOMAIN_FLAGS;

  constructor() {
    makeAutoObservable(this);
  }

  setAssessmentId(assessmentId: string | null): void {
    this.assessmentId = assessmentId;
  }

  setAssessment(assessment: GetAssessmentByIdResponse | null): void {
    this.assessment = assessment;
  }

  openExitModal(): void {
    this.isExitModalOpen = true;
  }

  closeExitModal(): void {
    this.isExitModalOpen = false;
  }

  openSaveChangesModal(): void {
    this.isSaveChangesModalOpen = true;
  }

  closeSaveChangesModal(): void {
    this.isSaveChangesModalOpen = false;
  }

  openClassificationWarning(): void {
    this.isClassificationWarningOpen = true;
  }

  closeClassificationWarning(): void {
    this.isClassificationWarningOpen = false;
  }

  setIsNavigationDisabled(value: boolean): void {
    this.isNavigationDisabled = value;
  }

  registerNodeClickGuard(handler: ((index: number) => void) | null): void {
    this.nodeClickGuard = handler;
  }

  registerExitGuard(handler: (() => void) | null): void {
    this.exitGuard = handler;
  }

  registerSaveChangesModalHandlers(handlers: SaveChangesModalHandlers | null): void {
    this.saveChangesModalHandlers = handlers;
  }

  registerClassificationWarningHandlers(
    handlers: ClassificationWarningHandlers | null,
  ): void {
    this.classificationWarningHandlers = handlers;
  }

  registerExitModalHandlers(handlers: ExitModalHandlers | null): void {
    this.exitModalHandlers = handlers;
  }

  setCurrentDomainFlags(flags: CurrentDomainFlags): void {
    this.currentDomainFlags = flags;
  }

  resetCurrentDomainFlags(): void {
    this.currentDomainFlags = DEFAULT_DOMAIN_FLAGS;
  }
}
